import { Card, CardContent } from "@/components/ui/card";
import { format, parseISO } from "date-fns";
import type { MarketingCampaign } from "@/types/sms";
import { isRotationFailed } from "@/utils/smsShared";
import type { BatchOutcome } from "@/hooks/sms/useInviteBatchOutcomes";

interface BatchTableProps {
  batches: MarketingCampaign[];
  outcomes: Record<string, BatchOutcome>;
  onOpenDetail: (campaign: MarketingCampaign) => void;
  isLoading: boolean;
}

function batchDateLabel(batch: MarketingCampaign): string {
  const raw = batch.invite_occurrence_date ?? batch.created_at;
  try {
    return format(parseISO(raw), "EEE MMM d");
  } catch {
    return raw.slice(0, 10);
  }
}

/**
 * The Last 14 days batch history: one row per day's invite campaign. Each row
 * opens the batch detail (who was texted, who was skipped and why). Sent/failed
 * come from the campaign row; joined/opted-out from the outcomes map.
 */
export function InviteBatchTable({ batches, outcomes, onOpenDetail, isLoading }: BatchTableProps) {
  return (
    <Card>
      <CardContent className="space-y-3 p-4 sm:p-6">
        <h3 className="text-base font-semibold text-foreground">Last 14 days</h3>
        {isLoading ? (
          <p className="py-4 text-sm text-muted-foreground">Loading batches…</p>
        ) : batches.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">
            No invite batches yet. The first one runs at the next daily send time once Order Invite is on.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[26rem] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th scope="col" className="py-2 pr-3 font-medium">Day</th>
                  <th scope="col" className="py-2 px-3 text-right font-medium">Invited</th>
                  <th scope="col" className="py-2 px-3 text-right font-medium">Joined</th>
                  <th scope="col" className="py-2 px-3 text-right font-medium">Opted out</th>
                  <th scope="col" className="py-2 pl-3 text-right font-medium">Failed</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((batch) => {
                  const outcome = outcomes[batch.id] ?? { joined: 0, optedOut: 0 };
                  return (
                    <tr key={batch.id} className="border-b border-border/60 last:border-0">
                      <td className="py-1.5 pr-3">
                        <button
                          type="button"
                          onClick={() => onOpenDetail(batch)}
                          className="font-medium text-action-primary underline-offset-2 hover:underline"
                        >
                          {batchDateLabel(batch)}
                        </button>
                        {isRotationFailed(batch.status, batch.audience_filter) && (
                          <span className="ml-2 rounded bg-status-danger/10 px-1.5 py-0.5 text-[10px] font-medium text-status-danger">
                            error
                          </span>
                        )}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums">{batch.sent_count}</td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-status-success">{outcome.joined}</td>
                      <td className="py-1.5 px-3 text-right tabular-nums">{outcome.optedOut}</td>
                      <td className="py-1.5 pl-3 text-right tabular-nums text-muted-foreground">{batch.failed_count}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
